// DiagiaParticipants — flat list of every invitee across all campaigns
// the admin owns. Each row shows status (invité / commencé / complété) +
// a copyable invite link that the admin can DM the participant.

import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  FaUsers,
  FaCopy,
  FaCheck,
  FaCheckCircle,
  FaPlayCircle,
  FaEnvelope,
} from 'react-icons/fa';

import useDiagiaApi from '../hooks/useDiagiaApi';

const STATUS_META = {
  invited:   { label: 'Invité',   className: 'is-draft',    icon: <FaEnvelope /> },
  started:   { label: 'Commencé', className: 'is-draft',    icon: <FaPlayCircle /> },
  completed: { label: 'Complété', className: 'is-active',   icon: <FaCheckCircle /> },
};

const buildInviteUrl = (token) =>
  `${window.location.origin}/invite/${token}`;

export default function DiagiaParticipants() {
  const { t } = useTranslation();
  const api = useDiagiaApi();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copiedToken, setCopiedToken] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const rows = await api.listParticipants();
      setItems(rows);
    } catch (e) {
      setError(e?.response?.data?.error || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const copy = async (token) => {
    try {
      await navigator.clipboard.writeText(buildInviteUrl(token));
      setCopiedToken(token);
      setTimeout(() => setCopiedToken((t) => (t === token ? '' : t)), 1800);
    } catch (e) {
      console.warn('clipboard copy failed', e);
    }
  };

  return (
    <div className="dia-page">
      <header className="dia-page-head">
        <div>
          <span className="dia-section-eyebrow">{t('diagia.nav.admin_section', 'Administration')}</span>
          <h1>{t('diagia.nav.participants', 'Participants')}</h1>
          <p>
            Suivez qui a reçu une invitation, qui a commencé son diagnostic,
            qui l'a complété.
          </p>
        </div>
      </header>

      {loading && <p className="dia-empty">{t('diagia.home.loading', 'Chargement…')}</p>}
      {!loading && error && <p className="dia-error">{error}</p>}

      {!loading && !error && items.length === 0 && (
        <div className="dia-empty-card">
          <FaUsers size={28} />
          <h3>{t('diagia.part.empty_title', "Aucun participant pour l'instant")}</h3>
          <p>{t('diagia.part.empty_body', 'Créez une campagne et ajoutez-y des invités pour les voir apparaître ici.')}</p>
          <Link to="/diagia/campaigns?new=1" className="dia-cta dia-cta-primary">
            Créer une campagne
          </Link>
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className="dia-card">
          <table className="dia-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>{t('diagia.part.th_campaign', 'Campagne')}</th>
                <th>{t('diagia.home.th_status', 'Statut')}</th>
                <th>{t('diagia.part.th_invited', 'Invité le')}</th>
                <th>{t('diagia.part.th_link', "Lien d'invitation")}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => {
                const meta = STATUS_META[p.status] || STATUS_META.invited;
                return (
                  <tr key={`${p.campaign_id}-${p.token}`}>
                    <td className="dia-table-title">{p.email}</td>
                    <td>
                      <Link to={`/diagia/campaigns/${p.campaign_id}`}>
                        {p.campaign_name || '—'}
                      </Link>
                    </td>
                    <td>
                      <span className={`dia-status ${meta.className}`}>
                        {meta.icon} {meta.label}
                      </span>
                    </td>
                    <td>{p.invited_at ? new Date(p.invited_at).toLocaleDateString('fr-FR') : '—'}</td>
                    <td>
                      {p.status === 'completed' ? (
                        <Link
                          to={`/diagia/diagnostics/${p.diagnostic_id}`}
                          className="dia-card-link"
                        >
                          Voir le diagnostic →
                        </Link>
                      ) : (
                        <button
                          type="button"
                          className="dia-cta dia-cta-ghost dia-cta-mini"
                          onClick={() => copy(p.token)}
                          title={t('diagia.part.copy_link', "Copier le lien d'invitation")}
                        >
                          {copiedToken === p.token ? <FaCheck /> : <FaCopy />}
                          {copiedToken === p.token ? 'Copié' : 'Copier le lien'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
